import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import apiService from '../services/apiService';

export default function SendMessageDialog({
  open,
  onClose,
  defaultChannel = 'sms',
  defaultTo = '',
  subjectType = null,
  subjectId = null,
  suggestJobId = null,
  suggestEstimateId = null,
}) {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [channel, setChannel] = useState(defaultChannel);
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadMeta = useCallback(async () => {
    try {
      const [st, tpls] = await Promise.all([
        apiService.get('communications/status'),
        apiService.get('message-templates?active=1'),
      ]);
      setStatus(st || null);
      setTemplates(Array.isArray(tpls) ? tpls : []);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setChannel(defaultChannel);
      setTo(defaultTo);
      setBody('');
      setSubject('');
      setTemplateId('');
      setError('');
      setSuccess('');
      loadMeta();
    }
  }, [open, defaultChannel, defaultTo, loadMeta]);

  const filteredTemplates = templates.filter((t) => t.channel === channel);

  async function applyTemplate(id) {
    if (!id) return;
    try {
      const vars = {};
      const rendered = await apiService.post(`message-templates/${id}/render`, vars);
      setBody(rendered.body || '');
      if (rendered.channel) setChannel(rendered.channel);
    } catch (e) {
      setError(e.message || 'Could not render template');
    }
  }

  async function suggestWithAi() {
    setBusy(true);
    setError('');
    try {
      const payload = {
        channel,
        body: body || undefined,
        message_template_id: templateId ? Number(templateId) : undefined,
      };
      if (suggestJobId) payload.collection_id = Number(suggestJobId);
      if (suggestEstimateId) payload.estimate_id = Number(suggestEstimateId);
      const res = await apiService.post('ai/suggest-message', payload);
      if (res.body) setBody(res.body);
    } catch (e) {
      setError(e.message || 'AI suggestion failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleSend() {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        channel,
        to: to.trim(),
        body: body.trim() || undefined,
        subject: channel === 'email' ? subject.trim() || undefined : undefined,
        message_template_id: templateId ? Number(templateId) : undefined,
      };
      if (subjectType && subjectId) {
        payload.subject_type = subjectType;
        payload.subject_id = subjectId;
      }
      const res = await apiService.post('communications/send', payload);
      if (res.ok) {
        setSuccess(res.message || 'Sent');
        setTimeout(() => onClose?.(), 800);
      } else {
        setError(res.message || 'Send failed');
      }
    } catch (e) {
      setError(e.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  }

  const smsReady = status?.manual_sms_enabled && status?.twilio_configured;
  const emailReady = status?.manual_email_enabled && status?.mail_configured;
  const channelReady = channel === 'sms' ? smsReady : emailReady;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send message</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {status && !channelReady ? (
          <Alert severity="info">
            {channel === 'sms'
              ? 'Enable manual SMS in Settings and configure TWILIO_* in .env.'
              : 'Enable manual email in Settings and configure MAIL_* (not log driver).'}
          </Alert>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <FormControl size="small">
          <InputLabel id="send-ch-label">Channel</InputLabel>
          <Select
            labelId="send-ch-label"
            label="Channel"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setTemplateId('');
            }}
          >
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>

        <TextField size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)} required />

        {channel === 'email' ? (
          <TextField size="small" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        ) : null}

        <FormControl size="small">
          <InputLabel id="send-tpl-label">Template (optional)</InputLabel>
          <Select
            labelId="send-tpl-label"
            label="Template (optional)"
            value={templateId}
            onChange={(e) => {
              const id = e.target.value;
              setTemplateId(id);
              if (id) applyTemplate(id);
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {filteredTemplates.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Message"
          multiline
          minRows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          helperText={templateId ? 'Template applied — edit before sending.' : 'Required if no template selected.'}
        />

        <Typography variant="caption" color="text.secondary">
          Logged in Message log. Daily limits: SMS {status?.sms_daily_limit ?? '—'}, email {status?.email_daily_limit ?? '—'}.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button size="small" onClick={suggestWithAi} disabled={busy}>
          Suggest with AI
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={busy || !to.trim() || (!body.trim() && !templateId) || !channelReady}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
